import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type OtterfolioPosition, fetchOtterfolioOverview } from "../lib/otterfolio";
import { KpiStrip } from "./otterfolio/KpiStrip";
import { OtterfolioLayout } from "./otterfolio/OtterfolioLayout";
import { PositionDetailModal } from "./otterfolio/PositionDetailModal";
import { PositionsGrid } from "./otterfolio/PositionsGrid";
import { TopBar } from "./otterfolio/TopBar";
import { ValueChartCard } from "./otterfolio/ValueChartCard";
import { VideoMode } from "./otterfolio/VideoMode";
import { WatchlistSection } from "./otterfolio/WatchlistSection";

export function OtterfolioPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["otterfolio", "overview"],
    queryFn: fetchOtterfolioOverview,
  });
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);
  const [videoMode, setVideoMode] = useState(false);

  if (isLoading) {
    return (
      <OtterfolioLayout>
        <div style={{ padding: 40, textAlign: "center", opacity: 0.7 }}>
          {t("otterfolio.loading")}
        </div>
      </OtterfolioLayout>
    );
  }
  if (isError || !data) {
    return (
      <OtterfolioLayout>
        <div style={{ padding: 40, textAlign: "center", opacity: 0.7 }}>
          {t("otterfolio.loadError")}
        </div>
      </OtterfolioLayout>
    );
  }
  if (data.positionsCount === 0) {
    return (
      <OtterfolioLayout>
        <TopBar totalValueCents={0} returnCents={0} returnPercent={0} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 20,
            padding: "60px 20px",
            textAlign: "center",
          }}
        >
          <img
            src="/otterfolio/otter-empty.png"
            alt=""
            style={{
              width: 280,
              maxWidth: "80%",
              height: "auto",
              borderRadius: 12,
              opacity: 0.95,
            }}
          />
          <div style={{ fontSize: 15, opacity: 0.75, maxWidth: 460 }}>{t("otterfolio.empty")}</div>
        </div>
        <WatchlistSection items={data.watchlist} />
      </OtterfolioLayout>
    );
  }

  const handlePositionClick = (p: OtterfolioPosition) => {
    setSelectedPositionId(p.id);
  };

  return (
    <OtterfolioLayout>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <TopBar
          totalValueCents={data.totalValueCents}
          returnCents={data.returnCents}
          returnPercent={data.returnPercent}
          categoryTotals={data.categoryTotals}
        />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={() => setVideoMode((v) => !v)}
            style={{
              background: "transparent",
              border: `1px solid var(--otterfolio-card-border, rgba(76,175,110,0.4))`,
              color: "inherit",
              padding: "6px 12px",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {videoMode ? "🎬 Standard-Ansicht" : "🎬 Video-Modus"}
          </button>
        </div>
        {videoMode ? (
          <VideoMode overview={data} />
        ) : (
          <>
            <KpiStrip overview={data} />
            <ValueChartCard valueHistory={data.valueHistory} benchmarkLabel={data.benchmarkLabel} />
            <PositionsGrid
              positions={data.positions}
              felsValueCents={data.fels.valueCents}
              raketeValueCents={data.rakete.valueCents}
              onPositionClick={handlePositionClick}
            />
            <WatchlistSection items={data.watchlist} />
          </>
        )}
      </div>
      <PositionDetailModal
        positionId={selectedPositionId}
        onClose={() => setSelectedPositionId(null)}
      />
    </OtterfolioLayout>
  );
}
